import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  flowMax,
} from 'ad-hok'
import { addEffectOnMount } from 'ad-hok-utils'
import cx from 'classnames'
import { range } from 'lodash'
import { ChangeEvent, FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi, addUser } from 'shared/src/api'
import { ProductUrl, UserListInput } from 'shared/src/api/types'
import {
  addIntPathParam,
  addNavigate,
  addOptionalIntPathParam,
} from 'shared/src/utils/addNavigate'
import { addNavigationState } from 'shared/src/utils/addNavigationState'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'

import Icon from 'components/Icon'
import { MAX_EDIT_NAME_LENGTH } from 'components/edits/EditSearch'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { addCmsLoadingAndErrorIndicators } from '../../utils/addCmsLoadingAndErrorIndicators'
import { makeCreatePostProductRoute } from '../../utils/routes'
import { Button } from '../Button'
import { IconButton } from '../IconButton'
import { Page } from '../Page'
import { Product } from '../post/Product'
import { ProductField, ProductFieldSkeleton } from '../post/ProductField'
import { ProductPickerButton } from '../post/ProductPickerButton'
import { ProductQueueModal } from '../post/ProductQueueModal'
import { SearchModal } from '../post/SearchModal'
import { Toggle } from '../Toggle'
import { addProductLinkingContext } from '../post/productLinking'
import {
  BODY_BOLD_STYLE,
  MAIN_TITLE_STYLE,
  NOTES_BOLD_STYLE,
  NOTES_MEDIUM_STYLE,
} from '../styles'
import { addToastContext } from '../toastContext'

const MIN_PRODUCTS = 4
const MAX_PRODUCTS = 20

const SEARCH_BUTTON_WIDTH = 56

const useStyles = createUseStyles({
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
  },
  nameInput: {
    backgroundColor: 'rgba(35, 35, 35, 0.02)',
    borderWidth: 1,
    borderColor: 'rgba(35, 35, 35, 0.08)',
    borderRadius: 2,
    ...MAIN_TITLE_STYLE,
    fontSize: 20,
    flex: 5.5,
    '&:focus': {
      backgroundColor: 'rgba(54, 52, 50, 0.04)',
      borderColor: 'rgba(144, 115, 79, 0.56)',
      outline: 'none !important',
    },
  },
  deleteButton: {
    display: 'flex',
    flex: 1,
    marginLeft: 8,
    backgroundColor: 'rgba(54, 52, 50, 0.02)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  productsLabel: {
    ...NOTES_BOLD_STYLE,
    color: '#90734F',
    maxWidth: 235,
  },
  productsTitleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 32,
    marginBottom: 11,
  },
  productsTitle: {
    ...BODY_BOLD_STYLE,
    color: 'rgba(54, 52, 50, 0.96)',
  },
  productsMinimumOrMaximum: {
    ...NOTES_MEDIUM_STYLE,
    color: 'rgba(54, 52, 50, 0.56)',
  },
  productsContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 16,
    gap: 8,
  },
  button: {
    marginTop: 34,
  },
  productFieldRowContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
  },
  productSearchButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: SEARCH_BUTTON_WIDTH,
    backgroundColor: 'rgba(54, 52, 50, 0.02)',
    borderRadius: 2,
    border: '1px solid rgba(144, 115, 79, 0.24)',
  },
  productSearchButtonSkeleton: {
    width: SEARCH_BUTTON_WIDTH,
  },
  buttonContainer: {
    display: 'flex',
    gap: 8,
  },
})

interface Props {
  editMode?: boolean
}

export const UserEditForm: FC<Props> = flowMax(
  addDisplayName('UserEditForm'),
  addApi,
  addOptionalIntPathParam('userId'),
  addDataLoadingFromPromise(
    'productQueue',
    ({ api, userId }) => api.products.queue(userId),
    ['api'],
    () => 'productQueue'
  ),
  addIntPathParam('resourceId'),
  addDataLoadingFromPromise(
    'userList',
    ({ api, resourceId }) => api.userLists.getForPublic(resourceId),
    ['resourceId']
  ),
  addCmsLoadingAndErrorIndicators('productQueue'),
  addCmsLoadingAndErrorIndicators('userList'),
  addNavigationState(
    'productIds',
    'setProductIds',
    ({ userList }) => userList?.products?.map((p) => p.id) ?? []
  ),
  addHandlers({
    addProductId:
      ({ productIds, setProductIds }) =>
      (productId: number) => {
        if (
          productIds.includes(productId) ||
          productIds.length >= MAX_PRODUCTS
        ) {
          return
        }

        setProductIds([...productIds, productId])
      },
    onDeleteProductId:
      ({ productIds, setProductIds }) =>
      (id: number) => {
        setProductIds(productIds.filter((existingId) => existingId !== id))
      },
  }),
  addNavigationState('isSearchModalOpen', 'setIsSearchModalOpen', false),
  addNavigationState('searchText', 'setSearchText', ''),
  addNavigationState(
    'isProductQueueModalOpen',
    'setIsProductQueueModalOpen',
    false
  ),
  addProductLinkingContext,
  addState('name', 'setName', ({ userList }) => userList.name),
  addState('isPublic', 'setIsPublic', ({ userList }) => userList.isPublic),
  addHandlers({
    onChangeName:
      ({ setName }) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        setName(event.target.value.substring(0, MAX_EDIT_NAME_LENGTH))
      },
    onChangeIsPublic:
      ({ userList, setIsPublic }) =>
      (val: boolean) => {
        userList.isPublic = val
        setIsPublic(val)
      },
  }),
  addEffectOnMount(
    ({
        linkProductId,
        clearLinkProductId,
        addProductId,
        setIsSearchModalOpen,
        setSearchText,
        setIsProductQueueModalOpen,
      }) =>
      () => {
        if (!linkProductId) return

        addProductId(linkProductId)

        clearLinkProductId()
        setIsSearchModalOpen(false)
        setSearchText('')
        setIsProductQueueModalOpen(false)
      }
  ),
  addState('isSaving', 'setSaving', false),
  addNavigate,
  addToastContext,
  addTranslationHelpers,
  addUser,
  addHandlers({
    onNewProductWithUrl:
      ({ navigate, userId }) =>
      (url: string) => {
        navigate(
          userId
            ? `/users/${userId}/userLists/products/create`
            : '/userLists/products/create',
          {
            state: {
              url,
            },
          }
        )
      },
    onSave:
      ({
        userList,
        name,
        api,
        resourceId,
        userId,
        productIds,
        setSaving,
        navigate,
        showErrorToast,
        showSuccessToast,
        t,
      }) =>
      async () => {
        setSaving(true)

        try {
          const input: UserListInput = {
            userId: userList.userId,
            name,
            editId: userList.editId,
            isPublic: userList.isPublic,
            productIds,
          }

          await api.userLists.update(resourceId, input)

          if (userId) {
            navigate(-1)
          } else {
            navigate('/userLists')
          }

          showSuccessToast({
            title: t('admin.userEdit.updatedTitle'),
            details: t('admin.userEdit.updatedDetail'),
          })
        } catch (error) {
          showErrorToast({
            title: t('admin.userEdit.errorSaving'),
            details: t('admin.general.genericError'),
          })
        } finally {
          setSaving(false)
        }
      },
    onDelete:
      ({
        api,
        resourceId,
        userId,
        navigate,
        setSaving,
        showSuccessToast,
        showErrorToast,
        t,
      }) =>
      async () => {
        setSaving(true)
        try {
          await api.userLists.delete(resourceId)

          if (userId) {
            navigate(-1)
          } else {
            navigate('/userLists')
          }
          showSuccessToast({
            title: t('admin.userEdit.deletedTitle'),
            details: t('admin.userEdit.deletedDetail'),
          })
        } catch (error) {
          showErrorToast({
            title: t('admin.userEdit.errorDeletingEdit'),
            details: t('admin.general.genericError'),
          })
        } finally {
          setSaving(false)
        }
      },
    onProductQueueClick:
      ({ setIsProductQueueModalOpen }) =>
      () => {
        setIsProductQueueModalOpen(true)
      },
    onProductUrlChoose:
      ({ addProductId, setIsProductQueueModalOpen, navigate, userId }) =>
      ({ id, url, product }: ProductUrl) => {
        if (product?.id) {
          addProductId(product.id)
          setIsProductQueueModalOpen(false)
        } else {
          navigate(makeCreatePostProductRoute(userId), {
            state: {
              url,
              productUrlId: id,
            },
          })
        }
      },
    onSearchProductChoose:
      ({ addProductId, setIsSearchModalOpen, setSearchText }) =>
      (productId: number) => {
        addProductId(productId)
        setIsSearchModalOpen(false)
        setSearchText('')
      },
    onProductClick:
      ({ navigate, user }) =>
      (productId: number) =>
      () => {
        if (user.isAdmin) {
          navigate(`/products/${productId}`)
        } else {
          navigate('/userLists/products/create', {
            state: {
              productId,
            },
          })
        }
      },
    onBack:
      ({ resourceId, editMode, api }) =>
      () => {
        if (!editMode) {
          api.userLists.delete(resourceId)
        }
      },
  }),
  addProps(({ isSaving, productIds }) => ({
    saveDisabled: isSaving || productIds.length < MIN_PRODUCTS,
  })),
  addProps(({ productIds }) => ({
    emptyProductFieldsToShow:
      productIds.length < MIN_PRODUCTS
        ? MIN_PRODUCTS - productIds.length
        : productIds.length < MAX_PRODUCTS
        ? 1
        : 0,
  })),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    userList,
    emptyProductFieldsToShow,
    productQueue,
    onNewProductWithUrl,
    productIds,
    onDeleteProductId,
    onSave,
    onDelete,
    saveDisabled,
    isSearchModalOpen,
    setIsSearchModalOpen,
    searchText,
    setSearchText,
    isProductQueueModalOpen,
    setIsProductQueueModalOpen,
    onProductQueueClick,
    onProductUrlChoose,
    onSearchProductChoose,
    onProductClick,
    onBack,
    name,
    onChangeName,
    onChangeIsPublic,
    isPublic,
    editMode,
    classes,
    t,
  }) => (
    <Page
      leftButton=""
      onBack={onBack}
      title={!!userList.edit ? userList.edit.name : ''}
      renderExtraBarContent={() => (
        <div className={cx(!userList.edit && classes.headerContainer)}>
          {!userList.edit && (
            <input
              value={name}
              onChange={onChangeName}
              className={cx(classes.nameInput, 'focus:ring-0')}
            />
          )}
          {editMode && (
            <button className={classes.deleteButton} onClick={onDelete}>
              <Icon name={'delete'} />
            </button>
          )}
        </div>
      )}
    >
      <Toggle
        label="Public: "
        value={isPublic || false}
        onChange={(val) => onChangeIsPublic(val)}
      />
      <div className={classes.productsTitleContainer}>
        <div className={classes.productsTitle}>
          {t('admin.userEdit.productsTitle')}
        </div>
        <div className={classes.productsMinimumOrMaximum}>
          {productIds.length > MIN_PRODUCTS
            ? t('admin.userEdit.productsMaximum', { max: MAX_PRODUCTS })
            : t('admin.userEdit.productsMinimum', { min: MIN_PRODUCTS })}
        </div>
      </div>
      <ProductPickerButton
        type="queue"
        titleStyle="long"
        number={productQueue.length}
        onClick={onProductQueueClick}
      />
      <div className={classes.productsContainer}>
        {productIds.map((id) => (
          <Product
            product={{ id }}
            onDelete={() => onDeleteProductId(id)}
            onClick={onProductClick(id)}
            key={id}
          />
        ))}
        {range(emptyProductFieldsToShow).map((index) => (
          <div className={classes.productFieldRowContainer} key={index}>
            {index > 0 ? (
              <ProductFieldSkeleton
                className={classes.productSearchButtonSkeleton}
              />
            ) : (
              <IconButton
                name="search"
                onClick={() => setIsSearchModalOpen(true)}
                className={classes.productSearchButton}
              />
            )}
            <ProductField
              onNewWithUrl={onNewProductWithUrl}
              disabled={index > 0}
            />
          </div>
        ))}
      </div>
      <div className={classes.buttonContainer}>
        <Button
          type="fullWidth"
          text={t('admin.userEdit.save')}
          icon="next"
          showIconAfterText
          onClick={onSave}
          disabled={saveDisabled}
          className={classes.button}
        />
      </div>
      {isSearchModalOpen && (
        <SearchModal
          onClose={() => setIsSearchModalOpen(false)}
          searchText={searchText}
          setSearchText={setSearchText}
          alreadySelectedProductIds={productIds}
          onProductChoose={onSearchProductChoose}
        />
      )}
      {isProductQueueModalOpen && (
        <ProductQueueModal
          queue={productQueue}
          onClose={() => {
            setIsProductQueueModalOpen(false)
          }}
          onUrlChoose={onProductUrlChoose}
        />
      )}
    </Page>
  )
)
